/**
 * objects.js - the object catalogue for Bubble Pop
 *
 * CONTENT, NOT MECHANICS. Nothing here knows what a bubble is, how a level is
 * paced, or how a component is judged. A future game supplies its own version
 * of this file and reuses everything else.
 *
 * ---------------------------------------------------------------------------
 * WHY EVERY OBJECT CARRIES AN `accept` LIST
 *
 * The recogniser returns dictionary words. It has no idea a two-year-old is
 * naming a picture, so it resolves ambiguity with a language model: "sun"
 * comes back as "son", "flower" as "flour", "shoe" as "shoo". The child said
 * the word correctly and the transcript says otherwise.
 *
 * So a target is not a string, it is a SET of strings that all mean "the child
 * said it". `word` is what the child is asked for and what Pip says; `accept`
 * is what counts as having said it. Anything a real recogniser plausibly
 * returns for a correct production belongs in `accept`; anything a DIFFERENT
 * word would also produce does not - "moo" is not in moon's list, because a
 * child saying "moo" has not said "moon".
 *
 * This is the single most important content rule in the file. See
 * docs/audit/GAME-01.md section 5.
 *
 * ---------------------------------------------------------------------------
 * AND WHY SOME PERFECTLY GOOD WORDS ARE NOT HERE
 *
 * The same language model that returns "son" for "sun" returns an obscenity for
 * a child's imprecise "fox". A real session stored `expected "fox", heard
 * "fuck"`. So a word is unusable here if its likely mis-recognition is
 * something no parent may see attributed to their child, whatever else it has
 * going for it.
 *
 * `duck`, `sock` and `kite` were in this catalogue and are not any more: duck
 * and sock for the obvious reasons, kite because it lands one final consonant
 * away from a slur. `engine/language.js` holds the screen, and a conformance
 * check runs it over this file - see docs/audit/GAME-02-CONTRACT.md section 7.
 *
 * ---------------------------------------------------------------------------
 * WHY THESE WORDS
 *
 * Every word is a concrete, picturable noun a child at Brown's Stage I would
 * plausibly have or be acquiring; one or two syllables; a consonant-vowel
 * shape that survives an approximate production; and no minimal pairs inside a
 * single level's pool, so a mis-recognition cannot be scored as a different
 * correct answer. `cat` and `hat` never appear in the same pool - discriminating
 * similar names is Phase 3 Level 4, which this game does not implement.
 */

/**
 * @typedef {object} GameObject
 * @property {string} id      stable key, used by levels and by the trial record
 * @property {string} word    what the child is asked to say; what Pip speaks
 * @property {string[]} accept every transcript token-string that counts as
 *                            having said it, `word` included, lowercase
 * @property {string} art     the rendered face of the object
 * @property {string} clue    Pip's semantic hint, one short sentence. Never
 *                            contains the word itself - a hint that says the
 *                            answer is a model, and models are a different
 *                            rung of the ladder.
 * @property {string} tint    bubble tint, so the field is varied without being
 *                            random noise
 * @property {string} [firstLetter] rendered at the partial_verbal rung.
 *                            Explicit rather than word[0] so "shoe" can show
 *                            "sh" - the letter is a pronunciation cue, not
 *                            spelling practice.
 */

export const OBJECTS = {
  apple:  { id: 'apple',  word: 'apple',  accept: ['apple', 'apples'],          art: '🍎', clue: 'It is red, and you can eat it.',        tint: '#ff8f8f', firstLetter: 'A' },
  ball:   { id: 'ball',   word: 'ball',   accept: ['ball', 'balls', 'bawl'],    art: '⚽', clue: 'It is round and it bounces.',          tint: '#8fb7ff', firstLetter: 'B' },
  dog:    { id: 'dog',    word: 'dog',    accept: ['dog', 'dogs'],              art: '🐶', clue: 'It says woof.',                        tint: '#f2c17a', firstLetter: 'D' },
  star:   { id: 'star',   word: 'star',   accept: ['star', 'stars'],            art: '⭐', clue: 'It shines up in the night sky.',       tint: '#ffd76e', firstLetter: 'St' },
  car:    { id: 'car',    word: 'car',    accept: ['car', 'cars'],              art: '🚗', clue: 'It goes vroom along the road.',        tint: '#7fd4c1', firstLetter: 'C' },
  cat:    { id: 'cat',    word: 'cat',    accept: ['cat', 'cats'],              art: '🐱', clue: 'It says meow.',                        tint: '#d3a7f0', firstLetter: 'C' },
  sun:    { id: 'sun',    word: 'sun',    accept: ['sun', 'son', 'suns'],       art: '☀️', clue: 'It is warm and bright in the day.',    tint: '#ffcf5c', firstLetter: 'S' },
  boat:   { id: 'boat',   word: 'boat',   accept: ['boat', 'boats'],            art: '⛵', clue: 'It floats on the water.',              tint: '#7fc4ff', firstLetter: 'B' },
  fish:   { id: 'fish',   word: 'fish',   accept: ['fish', 'fishes'],           art: '🐟', clue: 'It swims under the water.',            tint: '#6fd0e8', firstLetter: 'F' },
  bird:   { id: 'bird',   word: 'bird',   accept: ['bird', 'birds'],            art: '🐦', clue: 'It has wings and it flies.',           tint: '#9ee6c8', firstLetter: 'B' },
  cake:   { id: 'cake',   word: 'cake',   accept: ['cake', 'cakes'],            art: '🍰', clue: 'We eat it at a birthday party.',       tint: '#ffb2d0', firstLetter: 'C' },
  drum:   { id: 'drum',   word: 'drum',   accept: ['drum', 'drums'],            art: '🥁', clue: 'You hit it and it goes boom.',         tint: '#e8a06f', firstLetter: 'Dr' },
  moon:   { id: 'moon',   word: 'moon',   accept: ['moon', 'moons'],            art: '🌙', clue: 'It comes out when it is dark.',        tint: '#b5c4ff', firstLetter: 'M' },
  frog:   { id: 'frog',   word: 'frog',   accept: ['frog', 'frogs'],            art: '🐸', clue: 'It is green and it hops.',             tint: '#a8e06f', firstLetter: 'Fr' },
  hat:    { id: 'hat',    word: 'hat',    accept: ['hat', 'hats'],              art: '🎩', clue: 'It goes on top of your head.',         tint: '#ffe08a', firstLetter: 'H' },
  shoe:   { id: 'shoe',   word: 'shoe',   accept: ['shoe', 'shoes', 'shoo'],    art: '👟', clue: 'It goes on your foot.',                tint: '#9fb0c9', firstLetter: 'Sh' },
  train:  { id: 'train',  word: 'train',  accept: ['train', 'trains'],          art: '🚂', clue: 'It runs along the tracks.',            tint: '#8fa8ff', firstLetter: 'Tr' },
  flower: { id: 'flower', word: 'flower', accept: ['flower', 'flowers', 'flour'], art: '🌸', clue: 'It grows in the garden and smells sweet.', tint: '#ffa6c9', firstLetter: 'Fl' },
  cloud:  { id: 'cloud',  word: 'cloud',  accept: ['cloud', 'clouds'],          art: '☁️', clue: 'It is white and floats up in the sky.', tint: '#7fe0d8', firstLetter: 'Cl' },
  bear:   { id: 'bear',   word: 'bear',   accept: ['bear', 'bears', 'bare'],    art: '🧸', clue: 'It is soft, and you can hug it.',      tint: '#d8ad86', firstLetter: 'B' },

  // Added after the first playtest. Fifteen attempts at one level showed the
  // child four words, because the pools were four objects wide out of a
  // twenty-object catalogue. These are all single-syllable, high-frequency and
  // picturable, which is what Phase 1 needs; the pools that draw on them are
  // wider now.
  bus:    { id: 'bus',    word: 'bus',    accept: ['bus', 'buses', 'buss'],             art: '🚌', clue: 'Lots of people ride on it together.',  tint: '#ffc46b', firstLetter: 'B' },
  cup:    { id: 'cup',    word: 'cup',    accept: ['cup', 'cups'],              art: '🥤', clue: 'You drink out of it.',                 tint: '#9fd8ff', firstLetter: 'C' },
  key:    { id: 'key',    word: 'key',    accept: ['key', 'keys', 'quay'],      art: '🔑', clue: 'It opens the door.',                   tint: '#e8d38a', firstLetter: 'K' },
  egg:    { id: 'egg',    word: 'egg',    accept: ['egg', 'eggs'],              art: '🥚', clue: 'It is white and smooth, and it cracks.', tint: '#fff0d6', firstLetter: 'E' },
  tree:   { id: 'tree',   word: 'tree',   accept: ['tree', 'trees'],            art: '🌳', clue: 'It is tall and green and has leaves.',  tint: '#c7b3ff', firstLetter: 'Tr' },
  box:    { id: 'box',    word: 'box',    accept: ['box', 'boxes'],             art: '📦', clue: 'You can put things inside it.',        tint: '#d8b58a', firstLetter: 'B' },
  cow:    { id: 'cow',    word: 'cow',    accept: ['cow', 'cows'],              art: '🐄', clue: 'It says moo.',                         tint: '#e6e1d8', firstLetter: 'C' },
  pig:    { id: 'pig',    word: 'pig',    accept: ['pig', 'pigs'],              art: '🐷', clue: 'It is pink and it says oink.',         tint: '#ffc2d4', firstLetter: 'P' },
};

/**
 * The carrier words Phase 3 offers.
 *
 * `effect` is what the game DOES when this carrier is used, and it is the
 * reason a two-word mand is worth saying: "want apple" and "put apple" are
 * different requests and the world answers them differently. If both did the
 * same thing the second word would be decoration, and the child would learn
 * that only the noun matters - which is exactly what the audit found Block
 * Builder teaching (docs/audit/parts/10-speech-layer.md, B43).
 */
export const CARRIERS = {
  want: { id: 'want', word: 'want', accept: ['want', 'wants'], effect: 'give',   art: '🙌', label: 'want', pipLine: 'Pip will pass it to you.' },
  put:  { id: 'put',  word: 'put',  accept: ['put', 'puts'],   effect: 'place',  art: '🧺', label: 'put',  pipLine: 'It will go in the basket.' },
  more: { id: 'more', word: 'more', accept: ['more'],          effect: 'repeat', art: '➕', label: 'more', pipLine: 'Another one will float up.' },
};

/** @param {string} id @returns {GameObject|null} */
export function getObject(id) {
  return OBJECTS[id] || null;
}

/** @param {string} id @returns {object|null} */
export function getCarrier(id) {
  return CARRIERS[id] || null;
}

/**
 * Every string that counts as having produced this target.
 *
 * The engine's judgement.targetProduced() takes ONE target string, so a game
 * with homophone-tolerant targets has to call it once per variant. That loop
 * lives in ../../shared/phrase.js; this function is what supplies its input.
 *
 * @param {{objectId:string, carrierId?:string}} target
 * @returns {string[]} phrases, most canonical first
 */
export function acceptedPhrases({ objectId, carrierId }) {
  const object = getObject(objectId);
  if (!object) return [];
  if (!carrierId) return object.accept.slice();

  const carrier = getCarrier(carrierId);
  if (!carrier) return object.accept.slice();

  const out = [];
  for (const c of carrier.accept) {
    for (const n of object.accept) out.push(`${c} ${n}`);
  }
  return out;
}

/** The phrase Pip speaks and the prompt displays. */
export function canonicalPhrase({ objectId, carrierId }) {
  const object = getObject(objectId);
  if (!object) return '';
  const carrier = carrierId ? getCarrier(carrierId) : null;
  return carrier ? `${carrier.word} ${object.word}` : object.word;
}

export const OBJECT_IDS = Object.keys(OBJECTS);
export const CARRIER_IDS = Object.keys(CARRIERS);

export default { OBJECTS, CARRIERS, getObject, getCarrier, acceptedPhrases, canonicalPhrase, OBJECT_IDS, CARRIER_IDS };
